import { Flex, Skeleton, Space, Text, Tooltip } from "@/atoms"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { AiAdr, Runtime } from "@/icons"
import { CopyLinkButton } from "@/molecules/CopyLinkButton"
import { ShowMore } from "@/molecules/ShowMore"
import { TitleDrawer } from "@/molecules/TitleDrawer"
import { useWorkflowById } from "@/organisms/Drawers/applications/ai-discovery/useAiDiscovery"
import { useLink } from "@/organisms/Drawers/hooks/useLink"
import { WorkflowTypeCell } from "@/pages/AiDiscovery/components/molecules/WorkflowTypeCell"
import { WorkflowIcon } from "@/pages/AiDiscovery/components/molecules/WorkflowTypeCell/WorkflowTypeIcons"

import styles from "@/organisms/Drawers/components/AgentAiDiscoveryDrawer/components/TitleAgentAiDiscoveryDrawer/TitleAgentAiDiscoveryDrawer.module.scss"

import type { TitleAgentAiDiscoveryDrawerProps } from "../AgentAiDiscoveryDrawer/components/TitleAgentAiDiscoveryDrawer"

export const TitleWorkflowAiDiscoveryDrawer = (
  props: TitleAgentAiDiscoveryDrawerProps
) => {
  const { drawerId, record } = props
  const link = useLink(drawerId)
  const { t } = useTranslation()
  const { data, isLoading } = useWorkflowById({ record })
  // Binds the type badge's explanation once the workflow is here
  useTooltip(data)
  const { name, isGeneratedName, type, insights, apps, codeLocation } =
    data || {}

  if (isLoading)
    return (
      <Flex gap={8} className={styles.skeletonWrapper}>
        <Skeleton.Avatar active shape="square" />
        <Skeleton.Input active />
      </Flex>
    )

  return (
    <TitleDrawer
      loading={isLoading}
      icon={<WorkflowIcon />}
      title={
        <Flex gap={4} align="center">
          {name}
          {isGeneratedName && (
            <Tooltip title={t("inventory.ai.generatedName")}>
              <span className={styles.iconAi}>
                <AiAdr />
              </span>
            </Tooltip>
          )}
        </Flex>
      }
      subTitle={
        <Space
          separator={
            <Text type="secondary" className="n-4-color-text-divider-ks">
              |
            </Text>
          }
        >
          <WorkflowTypeCell type={type} t={t} />
          {!!apps?.items.length && (
            <Text type="secondary" className="sm-text-ks">
              {`${capitalizeFirstLetter(t("general.application", { count: apps?.items.length ?? 0 }))}: ${apps.label}`}
              {apps.count > 0 && (
                <ShowMore.Popover {...apps}>
                  <span>{` +${apps.count}`}</span>
                </ShowMore.Popover>
              )}
            </Text>
          )}
          {codeLocation && (
            <Text type="secondary" className="sm-text-ks">
              {`${codeLocation.filePath}:${codeLocation.line}`}
            </Text>
          )}
          {insights?.runtime && (
            <Flex align="center" gap={4}>
              <Runtime className={styles.icon} />
              <Text type="secondary" className="sm-text-ks">
                Running
              </Text>
            </Flex>
          )}
        </Space>
      }
      aside={<CopyLinkButton link={link} />}
    />
  )
}
